import React, { useState } from 'react';
import { Table, Tag, Button, Modal, Form, Input, Select, message, Space, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuthStore } from '../store/authStore';

const { Option } = Select;

const Users = () => {
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const currentUser = useAuthStore((state) => state.user);

    // Fetch users based on role
    const { data: usersData, isLoading } = useQuery('users', async () => {
        const endpoint = currentUser.role === 'superadmin' ? '/superadmin/users' : '/academy/users';
        const response = await api.get(endpoint);
        return response.data.data;
    });

    // Fetch academies for superadmin
    const { data: academiesData } = useQuery(
        'academies-for-users',
        async () => {
            const response = await api.get('/superadmin/academies');
            return response.data.data;
        },
        { enabled: currentUser.role === 'superadmin' }
    );

    const createMutation = useMutation(
        (data) => {
            const endpoint = currentUser.role === 'superadmin' ? '/superadmin/users' : '/academy/users';
            return api.post(endpoint, data);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('users');
                message.success(t('userCreatedSuccessfully'));
                setIsModalVisible(false);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToCreateUser'));
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }) => {
            const endpoint = currentUser.role === 'superadmin' ? `/superadmin/users/${id}` : `/academy/users/${id}`;
            return api.put(endpoint, data);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('users');
                message.success(t('userUpdatedSuccessfully'));
                setIsModalVisible(false);
                setEditingUser(null);
                form.resetFields();
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('failedToUpdateUser'));
            },
        }
    );

    const deleteMutation = useMutation(
        (id) => {
            const endpoint = currentUser.role === 'superadmin' ? `/superadmin/users/${id}` : `/academy/users/${id}`;
            return api.delete(endpoint);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('users');
                message.success(t('userDeletedSuccessfully'));
            },
            onError: (error) => {
                message.error(error.response?.data?.message || 'Failed to delete user');
            },
        }
    );

    const handleSubmit = (values) => {
        if (editingUser) {
            // Remove password if not changed
            const updateData = { ...values };
            if (!updateData.password) {
                delete updateData.password;
            }
            updateMutation.mutate({ id: editingUser._id, data: updateData });
        } else {
            createMutation.mutate(values);
        }
    };

    const handleEdit = (record) => {
        setEditingUser(record);
        form.setFieldsValue({
            ...record,
            academy: record.academy?._id || record.academy,
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('confirmDeleteUser'),
            content: t('actionCannotBeUndone'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const columns = [
        {
            title: t('name'),
            key: 'name',
            render: (_, r) => `${r.firstName} ${r.lastName}`
        },
        {
            title: t('email'),
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: t('phone'),
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: t('role'),
            dataIndex: 'role',
            key: 'role',
            render: (role) => {
                const colors = { superadmin: 'red', admin: 'blue', dataentry: 'green' };
                return <Tag color={colors[role]}>{role.toUpperCase()}</Tag>;
            }
        },
        ...(currentUser.role === 'superadmin' ? [
            {
                title: t('academy'),
                key: 'academy',
                render: (_, r) => r.academy?.name || 'N/A'
            }
        ] : []),
        {
            title: t('status'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? t('active') : t('inactive')}
                </Tag>
            )
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDelete(record._id)}
                        size="small"
                        disabled={record._id === currentUser.id}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="page-header">
                <h1>{t('users')}</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingUser(null);
                        form.resetFields();
                        setIsModalVisible(true);
                    }}
                    size="large"
                >
                    {t('addUser')}
                </Button>
            </div>

            <Table columns={columns} dataSource={usersData} rowKey="_id" loading={isLoading} />

            <Modal
                title={editingUser ? t('editUser') : t('addUser')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingUser(null);
                    form.resetFields();
                }}
                footer={null}
                width="min(700px, 95vw)"
                centered
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="firstName"
                                label={t('firstName')}
                                rules={[{ required: true, message: t('pleaseEnterFirstName') }]}
                            >
                                <Input placeholder="John" size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="lastName"
                                label={t('lastName')}
                                rules={[{ required: true, message: t('pleaseEnterLastName') }]}
                            >
                                <Input placeholder="Doe" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label={t('email')}
                                rules={[
                                    { required: true, message: t('pleaseEnterEmail') },
                                    { type: 'email', message: t('pleaseEnterValidEmail') },
                                ]}
                            >
                                <Input placeholder="user@example.com" size="large" disabled={!!editingUser} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label={t('phone')}
                            >
                                <Input placeholder="+1234567890" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="role"
                                label={t('role')}
                                rules={[{ required: true, message: t('pleaseSelectRole') }]}
                            >
                                <Select placeholder={t('selectRole')} size="large">
                                    {currentUser.role === 'superadmin' && (
                                        <Option value="superadmin">{t('superAdmin')}</Option>
                                    )}
                                    <Option value="admin">{t('admin')}</Option>
                                    <Option value="dataentry">{t('dataEntry')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            {currentUser.role === 'superadmin' && (
                                <Form.Item
                                    name="academy"
                                    label={t('academy')}
                                    rules={[
                                        {
                                            required: true,
                                            message: t('pleaseSelectAcademy'),
                                        },
                                    ]}
                                >
                                    <Select placeholder={t('selectAcademy')} size="large">
                                        {academiesData?.map((academy) => (
                                            <Option key={academy._id} value={academy._id}>
                                                {academy.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </Col>
                    </Row>

                    <Form.Item
                        name="password"
                        label={editingUser ? t('passwordLeaveBlank') : t('password')}
                        rules={[
                            {
                                required: !editingUser,
                                message: t('pleaseEnterPassword')
                            },
                            {
                                min: 6,
                                message: t('passwordMinLength')
                            },
                        ]}
                    >
                        <Input.Password placeholder="********" size="large" />
                    </Form.Item>

                    <Form.Item name="isActive" label={t('status')} initialValue={true}>
                        <Select size="large">
                            <Option value={true}>{t('active')}</Option>
                            <Option value={false}>{t('inactive')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24 }}>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={createMutation.isLoading || updateMutation.isLoading}
                            >
                                {editingUser ? t('updateUser') : t('createUser')}
                            </Button>
                            <Button onClick={() => setIsModalVisible(false)} size="large">
                                {t('cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Users;
